import { Controller } from "@hotwired/stimulus";
import { create, parseCreationOptionsFromJSON } from "@github/webauthn-json/browser-ponyfill";
import { conditionalMediationAvailable } from "../conditional_mediation_available";

// Expected, user-driven outcomes of a WebAuthn ceremony. The browser raises
// NotAllowedError when the prompt is dismissed or times out, and AbortError
// when one ceremony supersedes another. Neither is a fault — we tell the user
// and let them retry, and we deliberately do not let them reach the global
// AppSignal unhandledrejection handler.
const EXPECTED_WEBAUTHN_ERRORS = ["NotAllowedError", "AbortError"];

export default class extends Controller {
  static targets = ["credential", "status"];
  static values = {
    challengeUrl: String
  };

  connect() {
    this.submit = this.submit.bind(this); // Ensure correct context
    this.element.addEventListener("submit", this.submit);
  }

  disconnect() {
    this.element.removeEventListener("submit", this.submit);
  }

  async submit(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const available = await conditionalMediationAvailable();
    if (available) {
       await this.getChallengeAndSubmitCredential();  // Call with `this`
    } else {
      // Handle when conditional mediation is not available if needed
    }
  }

  async getChallengeAndSubmitCredential() {
    const csrfToken = document.getElementsByName("csrf-token")[0].content;
    let newChallengeURL = new URL(this.challengeUrlValue);

    try {
      const challengeFetch = await fetch(newChallengeURL, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken,
        },
      });

      const challengeJSON = await challengeFetch.json();
      if (challengeFetch.status === 403 && challengeJSON.reauth_required) {
        window.location.assign(challengeJSON.step_up_url);
        return;
      }
      if (!challengeFetch.ok) {
        this.showStatus("Please sign in again before adding a passkey.");
        return;
      }

      const credentialCreationOptions = parseCreationOptionsFromJSON({
        publicKey: challengeJSON,
      });

      const credentialCreationResponse = await create(credentialCreationOptions);

      this.credentialTarget.value = JSON.stringify(credentialCreationResponse);
      this.element.submit();
    } catch (error) {
      if (EXPECTED_WEBAUTHN_ERRORS.includes(error.name)) {
        // User cancelled or the prompt timed out — expected, not an error.
        this.showStatus("Passkey setup was cancelled or timed out. Please try again.");
        return;
      }

      // Genuinely unexpected — show a message, then let it surface to reporting.
      this.showStatus("Couldn't add your passkey. Please try again.");
      throw error;
    }
  }

  showStatus(message) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.textContent = message;
    this.statusTarget.classList.remove("hidden");
  }
}
